import { type PointerEvent, useCallback, useEffect, useRef, useState } from "react";

// Hold to record, slide left to cancel, let go to send. Keyboard users press once to start and
// get Send and Cancel buttons instead.

const CANCEL_DISTANCE = 90;
const SHORTEST = 0.5;

/** Opus in WebM or Ogg where the browser has it, and AAC in MP4 on Safari. */
export function recordingType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const types = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/webm", "audio/mp4"];
  return types.find((t) => MediaRecorder.isTypeSupported?.(t));
}

export type Recording = { elapsed: number; cancelling: boolean; keyboard: boolean };

type Session = {
  recorder: MediaRecorder | null;
  stream: MediaStream | null;
  chunks: Blob[];
  started: number;
  startX: number;
  /** Let go before the microphone was ready. */
  released: boolean;
  cancelling: boolean;
  keyboard: boolean;
};

export function useVoiceRecorder({
  onRecorded,
  onError,
}: {
  onRecorded: (blob: Blob, seconds: number) => void;
  onError: (message: string) => void;
}) {
  const [recording, setRecording] = useState<Recording | null>(null);
  const session = useRef<Session | null>(null);
  const callbacks = useRef({ onRecorded, onError });
  callbacks.current = { onRecorded, onError };

  const cleanup = useCallback(() => {
    const s = session.current;
    for (const track of s?.stream?.getTracks() ?? []) track.stop();
    session.current = null;
    setRecording(null);
  }, []);

  const finish = useCallback(
    (send: boolean) => {
      const s = session.current;
      if (!s) return;
      if (!s.recorder) {
        s.released = true;
        return;
      }
      const recorder = s.recorder;
      const seconds = (performance.now() - s.started) / 1000;
      recorder.onstop = () => {
        const blob = new Blob(s.chunks, { type: recorder.mimeType || recordingType() || "audio/webm" });
        cleanup();
        if (!send) return;
        if (seconds < SHORTEST) callbacks.current.onError("Hold the button down while you speak.");
        else callbacks.current.onRecorded(blob, seconds);
      };
      if (recorder.state !== "inactive") recorder.stop();
      else recorder.onstop?.(new Event("stop"));
    },
    [cleanup],
  );

  const start = useCallback(async (keyboard: boolean, startX = 0) => {
    if (session.current) return;
    const type = recordingType();
    if (!type || !navigator.mediaDevices?.getUserMedia) {
      callbacks.current.onError("This browser cannot record voice messages.");
      return;
    }
    const s: Session = {
      recorder: null,
      stream: null,
      chunks: [],
      started: performance.now(),
      startX,
      released: false,
      cancelling: false,
      keyboard,
    };
    session.current = s;
    try {
      s.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      session.current = null;
      callbacks.current.onError(
        "Rhema needs your microphone to record. Allow it in your browser's site settings and try again.",
      );
      return;
    }
    if (s.released || session.current !== s) {
      // They let go while the browser was asking for the microphone.
      for (const track of s.stream.getTracks()) track.stop();
      if (session.current === s) session.current = null;
      return;
    }
    const recorder = new MediaRecorder(s.stream, { mimeType: type });
    recorder.ondataavailable = (e) => {
      if (e.data.size) s.chunks.push(e.data);
    };
    recorder.start(250);
    s.recorder = recorder;
    s.started = performance.now();
    setRecording({ elapsed: 0, cancelling: false, keyboard });
  }, []);

  // The clock while recording.
  const active = recording !== null;
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => {
      const s = session.current;
      if (!s?.recorder) return;
      setRecording({
        elapsed: (performance.now() - s.started) / 1000,
        cancelling: s.cancelling,
        keyboard: s.keyboard,
      });
    }, 200);
    return () => clearInterval(timer);
  }, [active]);

  // Stop the microphone if the person leaves the chat mid-recording.
  useEffect(() => () => finish(false), [finish]);

  /** Pointer down on the microphone button. */
  const press = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      const startX = e.clientX;
      const move = (ev: globalThis.PointerEvent) => {
        const s = session.current;
        if (!s) return;
        const cancelling = startX - ev.clientX > CANCEL_DISTANCE;
        if (cancelling !== s.cancelling) {
          s.cancelling = cancelling;
          setRecording((r) => (r ? { ...r, cancelling } : r));
        }
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", cancel);
        finish(!session.current?.cancelling);
      };
      const cancel = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", cancel);
        finish(false);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", cancel);
      void start(false, startX);
    },
    [finish, start],
  );

  /** A keyboard press: start, or send what is recording. */
  const toggle = useCallback(() => {
    if (session.current) finish(true);
    else void start(true);
  }, [finish, start]);

  return {
    recording,
    press,
    toggle,
    stop: () => finish(true),
    cancel: () => finish(false),
  };
}
